import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { integrations } from '../data/mockData';

const Integrations = () => {
  const [activeTab, setActiveTab] = useState('pms');

  const lockersAccessControl = [...integrations.lockers, ...integrations.accessControl];

  const integrationCategories = [
    { id: 'pms', label: 'Property Management', count: integrations.pms.length },
    { id: 'pos', label: 'Point of Sale', count: integrations.pos.length },
    { id: 'spa', label: 'Spa & Leisure', count: integrations.spa.length },
    { id: 'lockersAccessControl', label: 'Lockers & Access Control', count: lockersAccessControl.length },
  ];

  return (
    <section id="integrations" className="py-24 bg-[#37464F] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Seamless Integration
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Connect with{' '}
            <span className="text-white">
              Your Existing Systems
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            WristCoin integrates with all major PMS, POS, and Lockers &amp; Access Control
          </p>
        </div>

        {/* Integration Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center gap-3 bg-transparent p-2 mb-12">
            {integrationCategories.map((category) => (
              <TabsTrigger
                key={category.id}
                value={category.id}
                className="
                  px-6 py-3 rounded-full font-medium transition-all duration-300
                  data-[state=inactive]:bg-[#37464F]/30 
                  data-[state=inactive]:text-gray-300 
                  data-[state=inactive]:border 
                  data-[state=inactive]:border-gray-700
                  data-[state=inactive]:hover:bg-[#37464F]/50
                  data-[state=inactive]:hover:border-[#009688]/30
                  data-[state=active]:bg-gradient-to-r 
                  data-[state=active]:from-[#009688] 
                  data-[state=active]:to-[#005B4F] 
                  data-[state=active]:text-white
                  data-[state=active]:shadow-lg
                  data-[state=active]:shadow-[#009688]/30
                "
              >
                {category.label}
                <span className="ml-2 text-xs opacity-70">({category.count})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* PMS Integrations */}
          <TabsContent value="pms" className="mt-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {integrations.pms.map((integration, index) => (
                <Card
                  key={index}
                  className="bg-[#edefef] border-gray-300 hover:border-[#009688]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#009688]/10 group"
                >
                  <CardContent className="p-6 flex items-center justify-center">
                    <div className="text-center w-full">
                      <div className="w-full h-20 flex items-center justify-center mb-3">
                        <img 
                          src={integration.logo} 
                          alt={integration.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* POS Integrations */}
          <TabsContent value="pos" className="mt-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {integrations.pos.map((integration, index) => (
                <Card
                  key={index}
                  className="bg-[#edefef] border-gray-300 hover:border-[#009688]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#009688]/10 group"
                >
                  <CardContent className="p-6 flex items-center justify-center">
                    <div className="text-center w-full">
                      <div className="w-full h-20 flex items-center justify-center mb-3">
                        <img
                          src={integration.logo}
                          alt={integration.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Spa & Leisure */}
          <TabsContent value="spa" className="mt-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {integrations.spa.map((integration, index) => (
                <Card
                  key={index}
                  className="bg-[#edefef] border-gray-300 hover:border-[#009688]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#009688]/10 group"
                >
                  <CardContent className="p-6 flex items-center justify-center">
                    <div className="text-center w-full">
                      <div className="w-full h-20 flex items-center justify-center mb-3">
                        <img
                          src={integration.logo}
                          alt={integration.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Lockers & Access Control */}
          <TabsContent value="lockersAccessControl" className="mt-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {lockersAccessControl.map((integration, index) => (
                <Card
                  key={index}
                  className="bg-[#edefef] border-gray-300 hover:border-[#009688]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#009688]/10 group"
                >
                  <CardContent className="p-6 flex items-center justify-center">
                    <div className="text-center w-full">
                      <div className="w-full h-20 flex items-center justify-center mb-3">
                        <img
                          src={integration.logo}
                          alt={integration.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">
            Don't see your system? We can integrate virtually with any platform.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('demo');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-[#009688] font-semibold hover:underline"
          >
            Contact us for custom integrations →
          </button>
        </div>
      </div>
    </section>
  );
};

export default Integrations;
